// --- src/data/m3/Unit5.js ---

export const M3_UNIT_5 = {
  id: "m3-u5-activities-verbs",
  title: "Unit 5: Activities & Free Time",
  topic: "GO + -ing, PLAY + noun, DO + noun",
  iconType: "running",
  grammarImage: "/m3-u5-activities.jpg",
  grammarText: "Master GO (swimming, running), PLAY (soccer, tennis), and DO (yoga, exercise). Learn when to use each verb and expand your free time vocabulary.",
  sets: [

    // ===================================================================
    // SET 1: GO + -ING EXPANDED (A2)
    // ===================================================================
    {
      id: "m3-u5-set1",
      title: "Set 1: GO + Activities 🏃",
      context: `*Lunes por la mañana - Universidad de Vancouver*

Carlos es estudiante de intercambio de México. Hoy habla sobre sus actividades.
"Los fines de semana voy a correr, juego ajedrez, y también hago yoga."
Sus compañeros británicos lo escuchan con interés.

*Monday morning - London University*

Carlos is an exchange student from Mexico. Today he talks about his activities.
"On weekends I go running, I play chess, and I also do yoga."
His British classmates listen with interest.`,
      
      questions: [
        {
          type: "match",
          q: "Match the activity to the correct verb pattern.",
          pairs: [
            { left: "do", right: "yoga🧘" },
            { left: "go", right: "running 🏃" },
            { left: "play", right: "chess ♟️" }
          ],
          a: "completed",
          tip: "GO + -ing for individual activities!",
          successTip: "'Do' se usa para actividades en general, 'go' para actividades que muchas veces terminan en -ing y play para deportes o juegos. 🎯"
        },
        {
          type: "mc",
          q: "Carlos says: 'I ___ swimming every morning.'",
          options: ["go", "play", "do"],
          a: "go",
          tip: "GO + -ing form!"
        },
        {
          type: "input",
          q: "Carlos: 'My friend ___ running in the park.'",
          a: "goes",
          alts: ["go"],
          tip: "Third person = goes!",
          successTip: "Goes! Third person -s added! 🏃"
        },
        {
          type: "order",
          q: "Traduce: 'Vamos a esquiar en invierno.'",
          words: ["We", "go", "skiing", "in", "winter."],
          a: "We go skiing in winter.",
          extra: ["play", "do", "skies"],
          tip: "GO skiing = winter activity!"
        },
        {
          type: "mc",
          q: "Carlos asks: 'Do you ___ surfing in summer?'",
          options: ["go", "play", "do"],
          a: "go",
          tip: "GO + -ing!",
          successTip: "GO surfing! Ocean activity! 🏄"
        },
        {
          type: "input",
          q: "Carlos: 'She ___ hiking every weekend.'",
          a: "goes",
          alts: ["go"],
          tip: "GO hiking = mountain activity!",
          successTip: "Goes hiking! Great exercise! 🥾"
        },
        {
          type: "order",
          q: "Traduce: 'Ellos van a pescar los domingos.'",
          words: ["They", "go", "fishing", "on", "Sundays."],
          a: "They go fishing on Sundays.",
          extra: ["play", "do", "fish"],
          tip: "GO fishing = water activity!",
          successTip: "Fish puede ser pescado o pesca! 🎣"
        },
        {
          type: "mc",
          q: "Carlos: 'I go ___ after class.'",
          options: ["skateboarding", "football", "karate"],
          a: "skateboarding",
          tip: "GO skateboarding!",
          successTip: "GO skateboarding! Individual activity! 🛹"
        },
        {
          type: "input",
          q: "Carlos plans: 'We ___ shopping on Saturday.'",
          a: "go",
          alts: [],
          tip: "GO shopping = activity!",
          successTip: "GO shopping! Common activity! 🛍️"
        },
        {
          type: "order",
          q: "Traduce: 'Ella va a bailar los viernes.'",
          words: ["She", "goes", "dancing", "on", "Fridays."],
          a: "She goes dancing on Fridays.",
          extra: ["play", "do", "dances"],
          tip: "GO dancing = nightlife!",
          successTip: "Goes dancing! Social activity! 💃"
        }
      ]
    },
{
      id: "m3-u5-set2",
      title: "Set 2: DO + Activities 🧘",
      context: `*Martes por la tarde - Gimnasio de la universidad*

Carlos descubre el gimnasio del campus.
"En México hago yoga y hago ejercicio regularmente."
El instructor británico sonríe: "Perfecto! Aquí hacemos yoga los martes y hacemos clases de ejercicio todos los días!"

*Tuesday afternoon - University gym*

Carlos discovers the campus gym.
"In Mexico I do yoga and do exercise regularly."
The British instructor smiles: "Perfect! Here we DO yoga on Tuesdays and DO exercise classes every day!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the activity to DO.",
          pairs: [
            { left: "Every day I ", right: "do yoga 🧘" },
            { left: "My friend ", right: "does exercise 💪" },
            { left: "The teacher play", right: "football⚽ " }
          ],
          a: "completed",
          tip: "DO for exercises and disciplines!",
          successTip: "DO + noun! Different from GO/PLAY! 🎯"
        },
        {
          type: "mc",
          q: "Carlos: 'I ___ yoga every morning.'",
          options: ["do", "go", "play"],
          a: "do",
          tip: "DO yoga!"
        },
        {
          type: "input",
          q: "Instructor: 'She ___ exercise three times a week.'",
          a: "does",
          alts: ["do"],
          tip: "Third person = does!",
          successTip: "Does! DO becomes DOES! 💪"
        },
        {
          type: "order",
          q: "Traduce: 'Hacemos gimnasia los lunes.'",
          words: ["We", "do", "gymnastics", "on", "Mondays."],
          a: "We do gymnastics on Mondays.",
          extra: ["go", "play", "gymnastic"],
          tip: "DO gymnastics!"
        },
        {
          type: "mc",
          q: "Carlos asks: 'Do you ___ martial arts?'",
          options: ["do", "go", "does","goes"],
          a: "do",
          tip: "DO martial arts!",
          successTip: "DO martial arts! Disciplines use DO! 🥋"
        },
        {
          type: "input",
          q: "Carlos: 'He ___ running in the morning.'",
          a: "goes",
          alts: ["go"],
          tip: "GO running!",
        },
        {
          type: "order",
          q: "Traduce: 'Ella hace pilates dos veces por semana.'",
          words: ["She", "does", "pilates", "twice", "a", "week."],
          a: "She does pilates twice a week.",
          extra: ["goes", "plays", "do"],
          tip: "DO pilates!"
        },
        {
          type: "mc",
          q: "Carlos: 'They ___ weightlifting at the gym.'",
          options: ["do", "go", "play"],
          a: "do",
          tip: "DO weightlifting!",
          successTip: "DO weightlifting! Gym activities! 🏋️"
        },
        {
          type: "input",
          q: "Carlos learns: 'I want to ___ karate.'",
          a: "do",
          alts: [],
          tip: "DO karate!",
          successTip: "DO karate! Martial arts use DO! 🥋"
        },
        {
          type: "order",
          q: "Traduce: 'Hacen crossfit juntos.'",
          words: ["They", "do", "CrossFit", "together."],
          a: "They do CrossFit together.",
          extra: ["go", "play", "goes"],
          tip: "DO CrossFit!",
          successTip: "DO CrossFit! Modern fitness! Together = juntos! 💪"
        }
      ]
    },
{
      id: "m3-u5-set3",
      title: "Set 3: PLAY + Sports ⚽",
      context: `*Miércoles - Campo deportivo*

Carlos se une a actividades deportivas del campus.
"En México juego fútbol y tenis. ¿Aquí puedo jugar?"
"¡Claro! jugamos fútbol los martes y tenis los jueves!" responde un estudiante.

*Wednesday - Sports field*

Carlos joins campus sports activities.
"In Mexico I play soccer and tennis. Can I play here?"
"Of course! We play soccer on Tuesdays and tennis on Thursdays!" responds a student.`,
      
      questions: [
        {
          type: "match",
          q: "Match the sport to PLAY.",
          pairs: [
            { left: "play", right: "soccer" },
            { left: "do", right: "exercise" },
            { left: "go", right: "swimming" }
          ],
          a: "completed",
          successTip: "PLAY + noun! Ball/team sports! 🎯"
        },
        {
          type: "mc",
          q: "Carlos: 'I ___ soccer every weekend.'",
          options: ["play", "go", "do"],
          a: "play",
          tip: "PLAY soccer!"
        },
        {
          type: "input",
          q: "Student: 'She ___ tennis on Thursdays.'",
          a: "plays",
          alts: ["play"],
          tip: "Third person = plays!",
          successTip: "Plays! PLAY becomes PLAYS! 🎾"
        },
        {
          type: "order",
          q: "Traduce: 'Jugamos baloncesto después de clase.'",
          words: ["We", "play", "basketball", "after", "class."],
          a: "We play basketball after class.",
          extra: ["go", "do", "plays"],
          tip: "PLAY basketball!"
        },
        {
          type: "mc",
          q: "Carlos asks: 'Do you ___ volleyball?'",
          options: ["play", "go", "do"],
          a: "play",
          tip: "PLAY volleyball!",
          successTip: "PLAY volleyball! Team sport! 🏐"
        },
        {
          type: "input",
          q: "Carlos: 'He ___ baseball in summer.'",
          a: "plays",
          alts: [],
          tip: "PLAY baseball!",
          successTip: "Plays baseball! American sport! ⚾"
        },
        {
          type: "order",
          q: "Traduce: 'Ella juega golf los domingos.'",
          words: ["She", "plays", "golf", "on", "Sundays."],
          a: "She plays golf on Sundays.",
          extra: ["goes", "does", "play"],
          tip: "PLAY golf (exception!)."
        },
        {
          type: "mc",
          q: "Carlos: 'They ___ yoga in the gym.'",
          options: ["play", "go", "do"],
          a: "do",
          tip: "DO yoga!",
       },
        {
          type: "input",
          q: "Carlos learns: 'We ___ badminton in the gym.'",
          a: "play",
          alts: [],
          tip: "PLAY badminton!",
          successTip: "PLAY badminton! Racket sport! 🏸"
        },
        {
          type: "order",
          q: "Traduce: 'Mis companeros juegan rugby en la universidad.'",
          words: ["My","classmates", "play", "rugby", "at", "university."],
          a: "My classmates play rugby at university.",
          extra: ["go", "do", "the"],
          tip: "PLAY rugby!",
          successTip: "PLAY rugby! Contact sport! At university = en la universidad! 🏉"
        }
      ]
    },

    // ===================================================================
    // SET 4: GO vs PLAY vs DO (A2+)
    // ===================================================================
    {
      id: "m3-u5-set4",
      title: "Set 4: Choosing the Right Verb",
      context: `*Jueves - Conversación en la cafetería*

Carlos habla con amigos sobre sus rutinas.
"Voy a nadar, juego tenis y hago yoga. ¿Es correcto?"
Sus amigos asientan: "Perfecto! Entiendes la diferencia!"

*Thursday - Cafeteria conversation*

Carlos talks with friends about his routines.
"I go swimming, I play soccer, and I do yoga. Is that correct?"
His friends nod: "Perfect! You understand the difference!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the activity to the correct verb.",
          pairs: [
            { left: "running 🏃", right: "GO" },
            { left: "tennis 🎾", right: "PLAY" },
            { left: "yoga 🧘", right: "DO" }
          ],
          a: "completed",
          tip: "Three different patterns!",
          successTip: "GO (-ing), PLAY (sports), DO (exercises)! 🎯"
        },
        {
          type: "mc",
          q: "Carlos: 'I ___ swimming and I ___ soccer.'",
          options: ["go / play", "play / go", "do / play"],
          a: "go / play",
          tip: "GO swimming, PLAY soccer!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ yoga and ___ tennis.'",
          a: "does plays",
          alts: ["does, plays", "does play","do play"],
          tip: "DO yoga, PLAY tennis!",
          successTip: "Does + plays! Two different verbs! 🧘🎾"
        },
        {
          type: "order",
          q: "Traduce: 'Vamos a correr y jugamos baloncesto.'",
          words: ["We", "go", "running", "and", "play", "basketball."],
          a: "We go running and play basketball.",
          extra: ["do", "plays", "goes"],
          tip: "GO running, PLAY basketball!"
        },
        {
          type: "mc",
          q: "Carlos: 'They ___ exercise and ___ cycling.'",
          options: ["do / go", "go / do", "play / go"],
          a: "do / go",
          tip: "DO exercise, GO cycling!",
          successTip: "DO / GO! Two patterns in one sentence! 💪🚴"
        },
        {
          type: "input",
          q: "Complete: 'He ___ karate but doesn't ___ soccer.'",
          a: "does play",
          alts: ["does play","do play","does plays"],
          tip: "DO karate, PLAY soccer!",
          successTip: "Does / play! Negative with PLAY! 🥋⚽"
        },
        {
          type: "order",
          q: "Traduce: 'Ella juega golf y hace pilates.'",
          words: ["She", "plays", "golf", "and", "does", "pilates."],
          a: "She plays golf and does pilates.",
          extra: ["goes", "but", "play"],
          tip: "PLAY golf, DO pilates!"
        },
        {
          type: "mc",
          q: "Carlos asks: 'Do you ___ chess?'",
          options: ["play", "go", "do"],
          a: "play",
          tip: "PLAY games/board games!",
          successTip: "PLAY chess! Board games use PLAY! ♟️"
        },
        {
          type: "input",
          q: "Complete: 'I ___ the guitar and ___ piano.'",
          a: "play play",
          alts: ["play, play", "play the", "plays plays the"],
          tip: "PLAY + the + instrument!",
          successTip: "PLAY the guitar/piano! Instruments use PLAY! 🎸🎹"
        },
        {
          type: "order",
          q: "Traduce: 'Hacemos ejercicio y vamos a nadar.'",
          words: ["We", "do", "exercise", "and", "go", "swimming."],
          a: "We do exercise and go swimming.",
          extra: ["play", "does", "plays"],
          tip: "DO exercise, GO swimming!",
          successTip: "Tambien puedes decir workout! para 'hacer ejercicio' 💪🏊"
        }
      ]
    },

    // ===================================================================
    // SET 5: READING PASSAGES - Short Stories (A2+)
    // ===================================================================
    {
      id: "m3-u5-set5",
      title: "Set 5: Reading - Activity Stories 📖",
      context: `*Viernes - Clase de inglés*

Carlos y sus compañeros leen historias cortas sobre actividades.
El profesor dice: "Lee estas historias cortas y responde preguntas."
Carlos lee con atención cada historia.

*Friday - English class*

Carlos and his classmates read short stories about activities.
The teacher says: "Read these short passages and answer questions."
Carlos reads each story carefully.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Mike's Weekend**

Hi! I'm Mike. On Saturday mornings, I go running in the park. It's relaxing! In the afternoon, I play soccer with friends. We play for two hours. On Sunday, I do yoga at home because it helps me relax after a busy week.`
        },
        {
          type: "mc",
          q: "What does Mike do on Saturday mornings?",
          options: ["He goes running", "He plays soccer", "He does yoga"],
          a: "He goes running",
          tip: "Saturday mornings = ?",
          successTip: "He goes running! Morning activity! 🏃"
        },
        {
          type: "input",
          q: "How long does Mike play soccer?",
          a: "two hours",
          alts: ["2 hours", "two", "2"],
          tip: "Check the Saturday afternoon sentence.",
          successTip: "Two hours! Good exercise! ⚽"
        },
        {
          type: "mc",
          q: "When does Mike do yoga?",
          options: ["On Sunday", "On Saturday", "Every day"],
          a: "On Sunday",
          tip: "Which day for yoga?"
        },
        {
          type: "passage",
          passage: `**Emma's Routine**

My name is Emma. I love sports! I go swimming three times a week at the pool. I also play tennis on Tuesday evenings. Tennis is my favorite sport. On weekends, I sometimes go cycling with my brother.`
        },
        {
          type: "mc",
          q: "How often does Emma go swimming?",
          options: ["Three times a week", "Every day", "Once a week"],
          a: "Three times a week",
          tip: "Count the frequency!",
          successTip: "Three times a week! Regular swimmer! 🏊"
        },
        {
          type: "input",
          q: "What is Emma's favorite sport?",
          a: "tennis",
          alts: [],
          tip: "Look for 'favorite'.",
          successTip: "Tennis! Her favorite! 🎾"
        },
        {
          type: "mc",
          q: "Who does Emma go cycling with?",
          options: ["Her brother", "Her friends", "Alone"],
          a: "Her brother",
          tip: "Weekend activity = ?"
        },
        {
          type: "passage",
          passage: `**Tom's Fitness**

I'm Tom. Every morning before work, I do exercise at the gym. I do weightlifting and sometimes I do aerobics. I don't play any team sports, but I go jogging on weekends. I prefer individual activities.`
        },
        {
          type: "mc",
          q: "When does Tom do exercise?",
          options: ["Every morning", "Every evening", "On weekends"],
          a: "Every morning",
          tip: "Before work = ?",
          successTip: "Every morning! Good habit! 💪"
        },
        {
          type: "input",
          q: "Does Tom play team sports?",
          a: "No",
          alts: ["no he doesn't", "no he does not"],
          tip: "Check 'I don't play...'",
          successTip: "No! He prefers individual activities! 🏃"
        },
        {
          type: "mc",
          q: "What does Tom prefer?",
          options: ["Individual activities", "Team sports", "Board games"],
          a: "Individual activities",
          tip: "Last sentence!",
          successTip: "Individual activities! Everyone has preferences! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 6: READING PASSAGES - Comparisons (B1-)
    // ===================================================================
    {
      id: "m3-u5-set6",
      title: "Set 6: Reading - Comparing Habits 📚",
      context: `*Lunes siguiente - Proyecto de clase*

Carlos y sus compañeros comparan rutinas de diferentes personas.
"Estos pasajes comparan actividades. Encuentre las diferencias!" explica el profesor.
Carlos analiza cuidadosamente.

*Next Monday - Class project*

Carlos and his classmates compare routines of different people.
"These passages compare activities. Find the differences!" explains the teacher.
Carlos analyzes carefully.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Two Friends**

Anna loves water! She goes swimming every day at 6 AM. She also plays water polo twice a week with her team. She says, "Water sports are amazing!"

Her friend Ben doesn't like water. He does martial arts three times a week. He plays basketball on weekends. He says, "I prefer sports on land!"`
        },
        {
          type: "mc",
          q: "What do Anna and Ben have in common?",
          options: ["They both play team sports", "They both swim", "They don't like sports"],
          a: "They both play team sports",
          tip: "Anna plays water polo, Ben plays basketball.",
          successTip: "Both play team sports! Water polo AND basketball! 🏐🏀"
        },
        {
          type: "input",
          q: "What time does Anna go swimming?",
          a: "6 AM",
          alts: ["6", "6am", "6 a.m.", "six am"],
          tip: "Very early morning!",
          successTip: "6 AM! Very early! ☀️🏊"
        },
        {
          type: "mc",
          q: "What does Ben do three times a week?",
          options: ["Martial arts", "Swimming", "Water polo"],
          a: "Martial arts",
          tip: "Ben's main activity = ?"
        },
        {
          type: "passage",
          passage: `**Brothers' Routine**

Jack and Sam are brothers. Jack goes running every morning because it wakes him up. He doesn't play any sports.

Sam plays soccer and basketball. He doesn't go running because he says it's boring. He prefers team activities.`
        },
        {
          type: "mc",
          q: "Why does Jack go running?",
          options: ["It wakes him up", "It's fun", "His brother does it"],
          a: "It wakes him up",
          tip: "'Because' gives the reason!",
          successTip: "It wakes him up! Morning energy! ☀️🏃"
        },
        {
          type: "input",
          q: "Does Sam go running?",
          a: "No",
          alts: ["no he doesn't", "no he does not"],
          tip: "He says it's boring!",
          successTip: "No! He thinks it's boring! Different preferences! 🏃❌"
        },
        {
          type: "mc",
          q: "What does Sam prefer?",
          options: ["Team activities", "Individual activities", "Water sports"],
          a: "Team activities",
          tip: "Last sentence!"
        },
        {
          type: "passage",
          passage: `**Past vs. Present**

Lisa used to play volleyball in high school. She played every day and loved it. But now she doesn't play volleyball anymore.

Now Lisa does yoga every morning and goes cycling on weekends. She says, "I prefer calm activities now."`
        },
        {
          type: "mc",
          q: "Does Lisa still play volleyball?",
          options: ["No, not anymore", "Yes, every day", "Only on weekends"],
          a: "No, not anymore",
          tip: "'Used to' = past habit!",
          successTip: "Not anymore! 'Used to' = past! 🏐→🧘"
        },
        {
          type: "input",
          q: "What does Lisa do every morning now?",
          a: "yoga",
          alts: ["she does yoga"],
          tip: "Current morning activity = ?",
          successTip: "Yoga! She changed her routine! 🧘"
        },
        {
          type: "mc",
          q: "How did Lisa's preferences change?",
          options: ["From active to calm sports", "From calm to active sports", "She still plays the same"],
          a: "From active to calm sports",
          tip: "Volleyball → yoga/cycling",
          successTip: "From active to calm! People change! 🔄"
        }
      ]
    },

    // ===================================================================
    // SET 7: FREQUENCY & NEGATIVES (A2+)
    // ===================================================================
    {
      id: "m3-u5-set7",
      title: "Set 7: How Often & Negatives ⏰",
      context: `*Martes - Conversación sobre frecuencias*

Carlos practica expresar frecuencias con negativas.
"No voy a nadar todos los días, pero lo hago dos veces a la semana."
El profesor aprueba: "Genial! Frecuencias con negativos!"

*Tuesday - Conversation about frequency*

Carlos practices expressing frequency with negatives.
"I don't go swimming every day, but I do it twice a week."
The teacher approves: "Great! Frequency with negatives!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the frequency expression.",
          pairs: [
            { left: "Once a week", right: "1 time per week" },
            { left: "Twice a month", right: "2 times per month" },
            { left: "Every day", right: "Daily" }
          ],
          a: "completed",
          tip: "Learn frequency words!",
          successTip: "Once, twice, every day! Essential! ⏰"
        },
        {
          type: "mc",
          q: "Carlos: 'I go swimming ___ a week.'",
          options: ["twice", "two", "second"],
          a: "twice",
          tip: "Twice = 2 times!"
        },
        {
          type: "input",
          q: "Complete: 'She plays tennis ___ a month.' (1 time)",
          a: "once",
          alts: ["one time"],
          tip: "Once = 1 time!",
          successTip: "Once! Once = 1, twice = 2! 📅"
        },
        {
          type: "order",
          q: "Traduce: 'Corro tres veces por semana.'",
          words: ["I", "go", "running", "three", "times", "a", "week."],
          a: "I go running three times a week.",
          extra: ["play", "twice", "month"],
          tip: "Number + times + period!"
        },
        {
          type: "mc",
          q: "Carlos: 'I ___ go swimming in winter.'",
          options: ["don't", "doesn't", "not"],
          a: "don't",
          tip: "Negative = don't!",
          successTip: "Don't! First person negative! ❌"
        },
        {
          type: "input",
          q: "Negative: 'She ___ play basketball.'",
          a: "doesn't",
          alts: ["does not","don't"],
          tip: "Third person = doesn't!",
          successTip: "Doesn't! Third person negative! 🏀❌"
        },
        {
          type: "order",
          q: "Traduce: 'No jugamos fútbol los lunes.'",
          words: ["We", "don't", "play", "soccer", "on", "Mondays."],
          a: "We don't play soccer on Mondays.",
          extra: ["doesn't", "not", "aren't"],
          tip: "Don't play!"
        },
        {
          type: "mc",
          q: "Carlos: 'They ___ do yoga anymore.'",
          options: ["don't", "doesn't","aren't"],
          a: "don't",
          tip: "Plural = don't!",
          successTip: "Don't! Anymore = ya no! 🧘❌"
        },
        {
          type: "input",
          q: "Complete: 'He ___ go running often.'",
          a: "doesn't",
          alts: ["does not","don't","do","does"],
          tip: "Doesn't + often = not frequently!",
          successTip: "Doesn't! Not often = rarely! 🏃"
        },
        {
          type: "order",
          q: "Traduce: 'Nunca juego tenis.'",
          words: ["I", "never", "play", "tennis."],
          a: "I never play tennis.",
          extra: ["don't", "not", "doesn't"],
          tip: "Never = nunca!",
          successTip: "Never! Never = 0 times! Strong negative! 🎾❌"
        }
      ]
    },

    // ===================================================================
    // SET 8: QUESTIONS & ANSWERS (A2+)
    // ===================================================================
    {
      id: "m3-u5-set8",
      title: "Set 8: Do You...? Questions",
      context: `*Miércoles - Práctica de preguntas*

Carlos practica hacer preguntas sobre actividades.
"Haces deporte? Vas a nadar? Tu amiga hace yoga?"
Sus compañeros responden con frases completas.

*Wednesday - Question practice*

Carlos practices asking questions about activities.
"Do you play sports? Do you go swimming? Does your friend do yoga?"
His classmates answer with complete phrases.`,
      
      questions: [
        {
          type: "match",
          q: "Match question to short answer.",
          pairs: [
            { left: "Do you play tennis?", right: "Yes, I do." },
            { left: "Does she go running?", right: "No, she doesn't." },
            { left: "Do they do yoga?", right: "Yes, they do." }
          ],
          a: "completed",
          tip: "Short answers use do/does!",
          successTip: "Do/Does questions! Short answers! 💬"
        },
        {
          type: "mc",
          q: "Carlos: '___ you play basketball?'",
          options: ["Do", "Does", "Are"],
          a: "Do",
          tip: "Do you...?"
        },
        {
          type: "input",
          q: "Question: '___ she go swimming?'",
          a: "Does",
          alts: [],
          tip: "Third person = Does!",
          successTip: "Does! Third person questions! ❓"
        },
        {
          type: "order",
          q: "Traduce: '¿Juegan fútbol los domingos?'",
          words: ["Do", "they", "play", "soccer", "on", "Sundays?"],
          a: "Do they play soccer on Sundays?",
          extra: ["Does", "Are", "Is"],
          tip: "Do + they + verb?"
        },
        {
          type: "mc",
          q: "Answer: 'Do you do yoga?' → 'Yes, ___'",
          options: ["I do", "I am", "I does"],
          a: "I do",
          tip: "Short answer: Yes, I do!",
          successTip: "I do! Short answer pattern! ✅"
        },
        {
          type: "input",
          q: "Answer: 'Does he play tennis?' → 'No, he ___'",
          a: "doesn't",
          alts: ["does not"],
          tip: "Negative short answer!",
          successTip: "Doesn't! Negative short answer! ❌"
        },
        {
          type: "order",
          q: "Traduce: '¿Con qué frecuencia vas a correr?'",
          words: ["How", "often", "do", "you", "go", "running?"],
          a: "How often do you go running?",
          extra: ["does", "are", "is"],
          tip: "How often = con qué frecuencia!"
        },
        {
          type: "mc",
          q: "Carlos: '___ sports do you like?'",
          options: ["What", "Who", "When"],
          a: "What",
          tip: "What sports = cuáles deportes!",
          successTip: "What! Question word for things! 🎯"
        },
        {
          type: "input",
          q: "Question: '___ does she do yoga?'",
          a: "When",
          alts: ["where", "how often"],
          tip: "Multiple question words work!",
          successTip: "When/Where/How often all work! Flexible! ❓"
        },
        {
          type: "order",
          q: "Traduce: '¿Tu hermano juega hockey?'",
          words: ["Does", "your", "brother", "play", "hockey?"],
          a: "Does your brother play hockey?",
          extra: ["Do", "Is", "Are"],
          tip: "Does + your brother...?",
          successTip: "Does your brother...? Family questions! 👨‍👦🏒"
        }
      ]
    },

    // ===================================================================
    // SET 9: NATURAL CONVERSATIONS (B1-)
    // ===================================================================
    {
      id: "m3-u5-set9",
      title: "Set 9: Real Conversations 💬",
      context: `*Jueves - Conversaciones naturales*

Carlos practica conversaciones completas sobre actividades.
"Que haces en tu tiempo libre? Voy a nadar y juego fútbol."
Sus respuestas suenan cada vez más naturales.

*Thursday - Natural conversations*

Carlos practices complete conversations about activities.
"What do you do in your free time? I go swimming and play soccer."
His answers sound increasingly natural.`,
      
      questions: [
        {
          type: "match",
          q: "Match question to natural response.",
          pairs: [
            { left: "Do you play any sports?", right: "Yeah, I play tennis twice a week." },
            { left: "What do you do for fun?", right: "I go swimming and cycling." },
            { left: "How often do you exercise?", right: "Every morning before work." }
          ],
          a: "completed",
          tip: "Natural responses add details!",
          successTip: "Natural conversations! Add details! 🗣️"
        },
        {
          type: "order",
          q: "Traduce: 'Sí, juego fútbol los fines de semana.'",
          words: ["Yeah,", "I", "play", "soccer", "on", "weekends."],
          a: "Yeah, I play soccer on weekends.",
          extra: ["Yes", "do", "go"],
          tip: "Yeah = informal yes!"
        },
        {
          type: "mc",
          q: "A: 'Do you go to the gym?' B: '___'",
          options: ["Not really, I prefer running outside.", "No, I like it", "Yes I am."],
          a: "Not really, I prefer running outside.",
          tip: "Add reason = natural!",
          successTip: "Natural! 'Not really' + reason! 💬"
        },
        {
          type: "input",
          q: "A: 'What do you do for fun?' B: 'I ___ swimming a lot.'",
          a: "go",
          alts: [],
          tip: "GO swimming!",
          successTip: "GO swimming! For fun = leisure! 🏊"
        },
        {
          type: "order",
          q: "Traduce: 'Juego baloncesto, pero solo en fin de semana.'",
          words: ["I", "play", "basketball,", "but", "only", "on", "weekends."],
          a: "I play basketball, but only on weekends.",
          extra: ["go", "do", "uses"],
        },
        {
          type: "mc",
          q: "A: 'Are you into sports?' B: '___'",
          options: ["Yeah, I'm really into tennis!", "Yes, I am.", "I like sports."],
          a: "Yeah, I'm really into tennis!",
          tip: "'Into' = interested in!",
          successTip: "'I'm into' es una forma de decir I'm interested in! 🎾"
        },
        {
          type: "input",
          q: "A: 'Do you play any instruments?' B: 'I ___ the guitar.'",
          a: "play",
          alts: [],
          tip: "PLAY instruments!",
          successTip: "PLAY the guitar! Instruments use PLAY! 🎸"
        },
        {
          type: "order",
          q: "Traduce: '¿En qué deportes estás interesado?'",
          words: ["What", "sports", "are", "you", "into?"],
          a: "What sports are you into?",
          extra: ["do", "does", "play"],
          tip: "'Into' = interested in!"
        },
        {
          type: "mc",
          q: "A: 'Want to play tennis later?' B: '___'",
          options: ["Sure! What time?", "Yes, I do.", "I want."],
          a: "Sure! What time?",
          tip: "Natural acceptance + question!",
          successTip: "Esta pregunta suena natural, aunque omite 'Do you' 👍"
        },
        {
          type: "input",
          q: "Casual invite: 'want to play ___ 🏀?'",
          a: "basketball",
          alts: ["football", "soccer", "tennis", "volleyball"],
          tip: "Let's = suggestion!",
          successTip: "Let's! Casual invitation! Want to/Wanna also work! 🏀"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTER PRACTICE (B1)
    // ===================================================================
    {
      id: "m3-u5-set10",
      title: "Set 10: Master All Verbs 🏆",
      context: `*Viernes - Evaluación final*

Carlos demuestra dominio de GO, PLAY y DO.
"Voy a correr, juego fútbol y hago yoga. Entiendo todo!"
El profesor aplaude: "Perfecto! Has dominado Unit 5!"

*Friday - Final evaluation*

Carlos demonstrates mastery of GO, PLAY, and DO.
"I go running, I play soccer, and I do yoga. I understand all three!"
The teacher applauds: "Perfect! You've mastered Unit 5!"`,
      
      questions: [
        {
          type: "match",
          q: "Final review: Match to correct verb.",
          pairs: [
            { left: "swimming, running, cycling", right: "GO" },
            { left: "tennis, soccer, basketball", right: "PLAY" },
            { left: "yoga, exercise, karate", right: "DO" }
          ],
          a: "completed",
          tip: "Three verb patterns!",
          successTip: "GO, PLAY, DO! All three mastered! 🏆"
        },
        {
          type: "order",
          q: "Traduce: 'Nado dos veces por semana y juego tenis los domingos.'",
          words: ["I", "go", "swimming", "twice", "a", "week", "and", "play", "tennis", "on", "Sundays."],
          a: "I go swimming twice a week and play tennis on Sundays.",
          extra: ["do", "does", "plays"],
          tip: "GO + PLAY in one sentence!",
          successTip: "Complex sentence! GO + PLAY combined! 💯"
        },
        {
          type: "mc",
          q: "Best answer: 'What do you do in your free time?'",
          options: ["I go running and play basketball.", "Running and basketball.", "I like sports."],
          a: "I go running and play basketball.",
          tip: "Complete sentence = natural!",
          successTip: "Complete answer! Natural! 🏃🏀"
        },
        {
          type: "input",
          q: "Complete: 'My sister ___ yoga, but I prefer to ___ running.'",
          a: "does go",
          alts: ["does, go"],
          tip: "DO yoga, GO running!",
          successTip: "Does / go! Two verbs in contrast! 🧘🏃"
        },
        {
          type: "order",
          q: "Traduce: '¿Quieres nadar o jugar tenis?'",
          words: ["Do", "you", "want", "to", "go", "swimming", "or", "play", "tennis?"],
          a: "Do you want to go swimming or play tennis?",
          extra: ["do", "does", "doing"],
          tip: "Want to + base verb!",
          successTip: "Invitation with options! Perfect! 🏊🎾"
        },
        {
          type: "mc",
          q: "Complete all three: 'I ___ yoga, ___ swimming, and ___ tennis.'",
          options: ["do / go / play", "play / go / do", "go / do / play"],
          a: "do / go / play",
          tip: "DO yoga, GO swimming, PLAY tennis!",
          successTip: "All three in one sentence! Master level! 🧘🏊🎾"
        },
        {
          type: "input",
          q: "Complete: 'I like to ___ soccer, but I hate to ___ running.'",
          a: "play go",
          alts: ["play, go"],
        },
        {
          type: "order",
          q: "Traduce: 'Ella no va al gimnasio, así que corre afuera.'",
          words: ["She", "doesn't", "go", "to", "the", "gym,", "so", "she", "goes", "running", "outside."],
          a: "She doesn't go to the gym, so she goes running outside.",
          extra: ["do", "play", "plays"],
          tip: "So = entonces/así que!",
          successTip: "So = result! Cause → result explained! 🏃"
        },
        {
          type: "mc",
          q: "Best response: 'Are you into sports?' → '___'",
          options: ["Yeah, I play tennis and go swimming regularly.", "Yes. I do", "I does like sports."],
          a: "Yeah, I play tennis and go swimming regularly.",
          tip: "Detailed = native level!",
          successTip: "Native-level response! Specific + detailed! 🌟"
        },
        {
          type: "input",
          q: "What's YOUR favorite activity? 'I like to ___ ____ '",
          a: "go swimming",
          alts: ["go running", "go cycling", "go skiing", "go hiking", "go fishing", "go surfing", "go skateboarding", "go shopping", "go dancing", "play tennis", "play soccer", "play basketball", "play football", "play volleyball", "play baseball", "play golf", "play hockey", "play badminton", "play chess", "play guitar", "play piano", "do yoga", "do exercise", "do karate", "do gymnastics"],
          tip: "Use GO/PLAY/DO correctly!",
          successTip: "Awesome! You can talk about YOUR activities! Unit 5 complete! 🎉"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Active Lifestyles
  // ===================================================================
  // ===================================================================
  // READING SET: Active Lifestyles (Divided by paragraphs)
  // ===================================================================
  readingSet: {
    id: "m3-u5-reading",
    title: "Reading: Alex's Active Life 🏃",
    category: "reading",

    questions: [
    {
        type: "passage",
        passage: `**Alex's Morning Routine**

Hi! I'm Alex and I love being active! On weekdays, I go running every morning at 6 AM before work. It wakes me up and gives me energy for the day. I run in the park near my house for about 30 minutes.`
      },
      {
        type: "mc",
        q: "When does Alex go running?",
        options: ["Every morning at 6 AM", "After work", "On weekends only"],
        a: "Every morning at 6 AM",
        tip: "Check the time mentioned in the passage.",
        successTip: "✓ EARLY BIRD!\nRunning at 6 AM is a great way to start the day! ☀️"
      },
      {
        type: "input",
        q: "Why does Alex go running?",
        a: "it wakes me up",
        alts: ["wakes me up", "gives me energy", "it gives me energy", "energy"],
        tip: "Look for 'because' or reasons mentioned.",
        successTip: "✓ MORNING ENERGY!\nExercise is a natural wake-up call! 💪"
      },
  {
        type: "passage",
        passage: `**Playing Tennis**

On Mondays and Wednesdays, I play tennis with my friend Sarah after work. Tennis is my favorite sport! We play for about an hour at the community courts. Sarah is really good, so she helps me improve my game.`
      },
      {
        type: "mc",
        q: "What is Alex's favorite sport?",
        options: ["Tennis", "Running", "Cycling"],
        a: "Tennis",
        tip: "Look for the word 'favorite'.",
        successTip: "✓ TENNIS!\nFavorite sport mentioned clearly! 🎾"
      },
      {
        type: "input",
        q: "How often does Alex play tennis per week?",
        a: "twice",
        alts: ["2 times", "two times", "2", "two"],
        tip: "Count the days: Mondays and Wednesdays.",
        successTip: "✓ TWICE A WEEK!\nTwo specific days = twice a week! 📅"
      },
      {
        type: "mc",
        q: "Who does Alex play tennis with?",
        options: ["Sarah", "His brother", "Alone"],
        a: "Sarah",
        tip: "Friend's name is mentioned.",
        successTip: "✓ SARAH!\nPlaying with friends makes sports more fun! 👫"
      },
   // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Yoga Break**

On Tuesdays and Thursdays, I do yoga during my lunch break. There's a yoga studio near my office, so it's very convenient. Yoga helps me relax and stay flexible. I usually do yoga for 45 minutes, then I have a quick lunch.`
      },
      {
        type: "input",
        q: "When does Alex do yoga?",
        a: "lunch break",
        alts: ["during lunch break", "lunch", "lunchtime", "at lunch"],
        tip: "Time of day mentioned in first sentence.",
        successTip: "✓ LUNCH BREAK!\nYoga during work breaks = smart time management! 🧘"
      },
      {
        type: "mc",
        q: "Where is the yoga studio?",
        options: ["Near his office", "Near his house", "At the gym"],
        a: "Near his office",
        tip: "Location is mentioned.",
        successTip: "✓ CONVENIENT!\nNear the office = easy to go! 🏢"
      },
{
        type: "passage",
        passage: `**Weekend Cycling**

On weekends, I sometimes go cycling with my brother. We ride for two hours on Saturday mornings in the countryside. It's beautiful and peaceful! We stop for coffee at a small café halfway through our ride.`
      },
      {
        type: "mc",
        q: "Who does Alex go cycling with?",
        options: ["His brother", "Sarah", "His coworkers"],
        a: "His brother",
        tip: "Family member mentioned.",
        successTip: "✓ BROTHER!\nFamily activities on weekends! 👨‍👦"
      },
      {
        type: "input",
        q: "When does Alex go cycling?",
        a: "Saturday mornings",
        alts: ["Saturday", "Saturdays", "weekend", "weekends"],
        tip: "Day and time mentioned.",
        successTip: "✓ SATURDAY MORNINGS!\nWeekend mornings in the countryside! 🚴🌳"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 5: Past vs Present (preguntas 10)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Changes Over Time**

I used to play basketball in college. I played on the university team and practiced every day. But I don't play basketball anymore. I prefer individual sports now because I can do them on my own schedule. Being active makes me healthy and happy!`
      },
      {
        type: "mc",
        q: "Does Alex still play basketball?",
        options: ["No, not anymore", "Yes, every day", "Yes, on weekends"],
        a: "No, not anymore",
        tip: "'Used to' = past habit, 'don't anymore' = stopped.",
        successTip: "✓ PAST HABIT!\n'Used to' means he played before but stopped! 🏀→❌"
      }
    ]
  },


  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m3-u5-review",
    title: "Unit 5 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: 'I ___ swimming.'",
        options: ["go", "play", "do"],
        a: "go",
        tip: "GO + -ing!"
      },
      {
        type: "input",
        q: "Review: 'She ___ tennis.'",
        a: "plays",
        alts: [],
        tip: "PLAY + sport!"
      },
      {
        type: "mc",
        q: "Review: 'They ___ yoga.'",
        options: ["do", "go", "play"],
        a: "do",
        tip: "DO + exercise!"
      },
      {
        type: "input",
        q: "Review: 'He ___ running.' (goes/plays/does)",
        a: "goes",
        alts: [],
        tip: "GO running!"
      },
      {
        type: "mc",
        q: "Review: 'I ___ basketball.'",
        options: ["play", "go", "do"],
        a: "play",
        tip: "PLAY + ball sport!"
      },
      {
        type: "input",
        q: "Review: 'We ___ exercise.' (verb)",
        a: "do",
        alts: [],
        tip: "DO exercise!"
      },
      {
        type: "order",
        q: "Review: Traduce: '¿Juegas fútbol?'",
        words: ["Do", "you", "play", "soccer?"],
        a: "Do you play soccer?",
        extra: ["go", "do", "does"],
        tip: "Do you + play?"
      },
      {
        type: "input",
        q: "Review: 'She ___ play tennis.' (negative)",
        a: "doesn't",
        alts: ["does not"],
        tip: "Doesn't = negative!"
      },
      {
        type: "mc",
        q: "Review: 'Once a week' = ___ time per week.",
        options: ["1", "2", "3"],
        a: "1",
        tip: "Once = 1 time!"
      },
      {
        type: "mc",
        q: "Review: Which is correct?",
        options: ["I go running and play tennis.", "I play running and go tennis.", "I do running and do tennis."],
        a: "I go running and play tennis.",
        tip: "GO running, PLAY tennis!",
        successTip: "🏆 UNIT 5 COMPLETE! GO, PLAY, DO mastered! Ready for Unit 6! 🚀"
      }
    ]
  }
};
